import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Flex, Grid, Text, Button, ButtonGroup, HStack, SimpleGrid, Badge, Select, Input, Textarea,
    InputGroup, InputLeftElement, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Spinner, Tooltip,
    Alert, AlertIcon, Progress, useToast, useDisclosure, FormControl, FormLabel, FormHelperText,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, UnorderedList, ListItem,
} from '@chakra-ui/react';
import { FlaskConical, RefreshCw, Play, Square, Search } from 'lucide-react';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getBotAbTestes, getBotAbResultado, iniciarBotAbTeste, encerrarBotAbTeste } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const ACCENT = '#F68633';
const ACCENT_HOVER = '#e0752a';

// Uma cor por braço, a mesma em todos os gráficos da tela (a cor segue o braço,
// nunca a posição). Par validado na superfície clara: daltonismo ΔE 24,7 e
// visão normal 33,6; contraste ≥ 3:1. O ADM roda só no tema claro.
const VARIANTES = ['A', 'B'];
const COR = { A: '#2a78d6', B: '#eb6834' };
const GRADE = '#e1e0d9';
const TINTA_MUTED = '#898781';
const COR_MELHOR = '#006300';

const POR_PAGINA = 25;
const ATUALIZAR_A_CADA_MS = 120000;

const TOOL_LABEL = {
    buscar_produto: 'Buscar produto',
    buscar_por_aplicacao: 'Buscar por aplicação',
    consultar_estoque: 'Consultar estoque',
    calcular_frete: 'Calcular frete',
    pesquisar_web: 'Pesquisar na web',
    visitar_pagina: 'Visitar página',
    gerar_orcamento_pdf: 'Orçamento em PDF',
    gerar_link_carrinho_automatico: 'Link de carrinho',
    obter_link_mercado_livre: 'Link do Mercado Livre',
    enviar_foto_produto: 'Enviar foto ao cliente',
    ver_foto_produto: 'Ver foto do produto',
    extrair_mais_frames_video: 'Mais quadros de vídeo',
    transferir_departamento: 'Transferir de setor',
    solicitar_tarefa_humana: 'Tarefa para a equipe',
};

const STATUS_CONTATO = {
    convertido: { label: 'Comprou', scheme: 'green' },
    em_janela: { label: 'Janela aberta', scheme: 'blue' },
    nao_convertido: { label: 'Não comprou', scheme: 'gray' },
    sem_telefone: { label: 'Sem telefone', scheme: 'yellow' },
};

const TIPOS_TOKEN = [
    { chave: 'input', label: 'Entrada', nota: 'prompt sem cache' },
    { chave: 'output', label: 'Saída', nota: 'texto e raciocínio gerados' },
    { chave: 'cache_read', label: 'Cache lido', nota: 'prompt reaproveitado' },
    { chave: 'cache_write', label: 'Cache gravado', nota: 'prompt gravado no cache' },
];

const vazio = (v) => v === null || v === undefined || Number.isNaN(v);
const fmtNum = (v, casas = 1) => (vazio(v) ? '—'
    : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas }));
const fmtInt = (v) => (vazio(v) ? '—' : Math.round(Number(v)).toLocaleString('pt-BR'));
const fmtMoeda = (simbolo, v, casas) => (vazio(v) ? '—' : `${Number(v) < 0 ? '−' : ''}${simbolo} ${fmtNum(Math.abs(v), casas)}`);
const fmtUsd = (v, casas = 2) => fmtMoeda('US$', v, casas);
const fmtBrl = (v, casas = 2) => fmtMoeda('R$', v, casas);
const fmtPct = (v, casas = 1) => (vazio(v) ? '—' : `${fmtNum(v, casas)}%`);
const fmtTaxa = (p) => (vazio(p) ? '—' : fmtPct(p * 100));
const fmtSeg = (s) => (vazio(s) ? '—' : `${fmtNum(s, s < 10 ? 1 : 0)} s`);
const fmtP = (p) => (vazio(p) ? '—' : p < 0.001 ? '< 0,001' : fmtNum(p, 3));
const fmtDataHora = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)} ${s.slice(11, 16)}` : '—');
const fmtDia = (s) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;
const nomeBraco = (b, v) => `${v} · ${b?.rotulo || '-'}`;

/** Qual braço tem o melhor valor ('maior' ou 'menor' é melhor); null se empate ou sem dado. */
const qualMelhor = (direcao, a, b) => {
    if (!direcao || vazio(a) || vazio(b) || Number(a) === Number(b)) return null;
    const aGanha = direcao === 'maior' ? Number(a) > Number(b) : Number(a) < Number(b);
    return aGanha ? 'A' : 'B';
};

/** Diferença de A para B: em pontos percentuais (taxas) ou relativa (o resto). */
const textoDelta = (tipo, a, b) => {
    if (vazio(a) || vazio(b)) return '—';
    if (tipo === 'pp') {
        const d = (Number(a) - Number(b)) * 100;
        return `${d > 0 ? '+' : d < 0 ? '−' : ''}${fmtNum(Math.abs(d), 1)} p.p.`;
    }
    if (Number(b) === 0) return '—';
    const rel = (Number(a) / Number(b) - 1) * 100;
    return `${rel > 0 ? '+' : rel < 0 ? '−' : ''}${fmtNum(Math.abs(rel), 0)}%`;
};

const Card = ({ titulo, subtitulo, extra, children, ...rest }) => (
    <Box bg="white" borderWidth="1px" borderRadius="lg" p={{ base: 3, md: 4 }} {...rest}>
        {(titulo || extra) && (
            <Flex align="flex-start" justify="space-between" mb={3} wrap="wrap" gap={2}>
                <Box minW={0}>
                    <Text as="h2" fontWeight="semibold">{titulo}</Text>
                    {subtitulo && <Text fontSize="xs" color="gray.500">{subtitulo}</Text>}
                </Box>
                {extra}
            </Flex>
        )}
        {children}
    </Box>
);

const Ponto = ({ cor, tamanho = '10px' }) => (
    <Box w={tamanho} h={tamanho} borderRadius="full" bg={cor} flexShrink={0} aria-hidden="true" />
);

/** Legenda dos dois braços — a identidade nunca depende só da cor. */
const LegendaBracos = ({ bracos }) => (
    <HStack spacing={4} wrap="wrap">
        {VARIANTES.map((v) => (
            <HStack key={v} spacing={1.5}>
                <Ponto cor={COR[v]} />
                <Text fontSize="xs" color="gray.600">{nomeBraco(bracos?.[v], v)}</Text>
            </HStack>
        ))}
    </HStack>
);

// ─────────────────────────── Estado do teste ───────────────────────────

const BannerStatus = ({ teste, ativoExiste, rotuloPadrao, modelos, onIniciar, onEncerrar }) => {
    if (!teste) {
        return (
            <Box bg="white" borderWidth="1px" borderRadius="lg" p={{ base: 4, md: 5 }} mb={4}>
                <Flex gap={4} align={{ base: 'stretch', md: 'center' }} direction={{ base: 'column', md: 'row' }}>
                    <Box flex="1" minW={0}>
                        <Text fontWeight="semibold">Nenhum teste A/B rodando</Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                            Hoje o bot atende 100% dos contatos no {rotuloPadrao}. Ao iniciar, cada contato que o bot
                            atender é sorteado 50/50 entre os dois modelos e fica no mesmo modelo até o fim do teste.
                        </Text>
                    </Box>
                    <Button leftIcon={<Play size={16} />} bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} onClick={onIniciar}>
                        Iniciar teste A/B
                    </Button>
                </Flex>
            </Box>
        );
    }

    const ativo = teste.status === 'ativo';
    const rotulo = (m) => modelos?.[m] || m;
    return (
        <Box bg="white" borderWidth="1px" borderRadius="lg" p={{ base: 4, md: 5 }} mb={4}
            borderLeftWidth="4px" borderLeftColor={ativo ? 'green.500' : 'gray.300'}>
            <Flex gap={4} align={{ base: 'stretch', md: 'center' }} direction={{ base: 'column', md: 'row' }}>
                <Box flex="1" minW={0}>
                    <HStack spacing={2} wrap="wrap" mb={1}>
                        <Badge colorScheme={ativo ? 'green' : 'gray'} fontSize="0.75em" px={2} py={0.5}>
                            {ativo ? 'Em andamento' : 'Encerrado'}
                        </Badge>
                        <Text fontWeight="semibold" noOfLines={1}>{teste.nome}</Text>
                    </HStack>
                    <Text fontSize="sm" color="gray.600">
                        {ativo
                            ? `Desde ${fmtDataHora(teste.iniciado_em)} · sorteio 50/50 por contato · cada contato fica no mesmo modelo até o fim.`
                            : `${fmtDataHora(teste.iniciado_em)} → ${fmtDataHora(teste.encerrado_em)} · o bot voltou para o ${rotuloPadrao}.`}
                    </Text>
                    <HStack spacing={4} mt={2} wrap="wrap">
                        {VARIANTES.map((v) => (
                            <HStack key={v} spacing={1.5}>
                                <Ponto cor={COR[v]} />
                                <Text fontSize="sm">
                                    <b>{v}</b> · {rotulo(v === 'A' ? teste.modelo_a : teste.modelo_b)}
                                    <Text as="span" color="gray.500"> — {fmtInt(teste.contatos?.[v])} contatos</Text>
                                </Text>
                            </HStack>
                        ))}
                    </HStack>
                    {teste.observacao && <Text fontSize="xs" color="gray.500" mt={2}>{teste.observacao}</Text>}
                </Box>
                {ativo && (
                    <Button leftIcon={<Square size={14} />} variant="outline" colorScheme="red" onClick={onEncerrar}>
                        Encerrar teste
                    </Button>
                )}
                {!ativo && !ativoExiste && (
                    <Button leftIcon={<Play size={16} />} bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} onClick={onIniciar}>
                        Iniciar novo teste
                    </Button>
                )}
            </Flex>
        </Box>
    );
};

// ─────────────────────────── Veredito ───────────────────────────

/** Taxa de cada braço com o intervalo de 95%, numa escala comum (lado a lado). */
const FaixaConfianca = ({ bracos }) => {
    const topo = Math.max(0.05, ...VARIANTES.map((v) => bracos[v].ic95?.[1] || 0)) * 1.1;
    const pos = (x) => Math.max(0, Math.min(100, (x / topo) * 100));
    return (
        <Box>
            {VARIANTES.map((v) => {
                const b = bracos[v];
                const temDado = !vazio(b.taxa_conversao) && b.ic95;
                const rotuloIc = temDado
                    ? `${nomeBraco(b, v)}: ${fmtTaxa(b.taxa_conversao)} — intervalo de 95%: ${fmtTaxa(b.ic95[0])} a ${fmtTaxa(b.ic95[1])}`
                    : `${nomeBraco(b, v)}: sem contatos com a janela fechada`;
                return (
                    <Flex key={v} align="center" gap={3} py={1.5}>
                        <HStack w={{ base: '112px', md: '128px' }} flexShrink={0} spacing={1.5}>
                            <Ponto cor={COR[v]} />
                            <Text fontSize={{ base: 'xs', md: 'sm' }} noOfLines={1}>{nomeBraco(b, v)}</Text>
                        </HStack>
                        <Tooltip label={rotuloIc} hasArrow>
                            <Box position="relative" flex="1" h="24px" tabIndex={0} aria-label={rotuloIc}>
                                <Box position="absolute" top="11px" left={0} right={0} h="2px" bg="gray.100" />
                                {temDado && (
                                    <>
                                        <Box position="absolute" top="8px" h="8px" borderRadius="full" bg={COR[v]} opacity={0.3}
                                            left={`${pos(b.ic95[0])}%`} w={`${Math.max(1, pos(b.ic95[1]) - pos(b.ic95[0]))}%`} />
                                        <Box position="absolute" top="5px" w="14px" h="14px" ml="-7px" borderRadius="full"
                                            bg={COR[v]} border="2px solid white" left={`${pos(b.taxa_conversao)}%`} />
                                    </>
                                )}
                            </Box>
                        </Tooltip>
                        <Text w="56px" textAlign="right" fontSize="sm" fontWeight="semibold">{fmtTaxa(b.taxa_conversao)}</Text>
                    </Flex>
                );
            })}
            <Flex justify="space-between" ml={{ base: '124px', md: '140px' }} mr="68px" mt={0.5}>
                <Text fontSize="xs" color="gray.400">0%</Text>
                <Text fontSize="xs" color="gray.400">{fmtPct(topo * 100, 0)}</Text>
            </Flex>
        </Box>
    );
};

const VereditoConversao = ({ r }) => {
    const { comparativo: c, bracos, parametros } = r;
    const { A, B } = bracos;
    const minimo = parametros.amostra_minima;
    const janela = parametros.janela_conversao_horas;

    let tom;
    let titulo;
    let texto;
    if (c.menor_amostra < minimo) {
        tom = 'gray';
        titulo = 'Amostra ainda pequena';
        texto = `${fmtInt(c.menor_amostra)} contato(s) com a janela de ${janela}h fechada no menor braço; o mínimo para ler o resultado é ${minimo}. Até lá os números oscilam muito.`;
    } else if (!vazio(c.p_valor) && c.p_valor < 0.05) {
        const venceA = c.taxa_a > c.taxa_b;
        const [ganha, perde] = venceA ? [A, B] : [B, A];
        tom = 'green';
        titulo = `${ganha.rotulo} converte mais`;
        texto = `${fmtTaxa(ganha.taxa_conversao)} × ${fmtTaxa(perde.taxa_conversao)} (${fmtNum(Math.abs(c.diferenca_pp), 1)} p.p., p = ${fmtP(c.p_valor)}): dificilmente é acaso.`
            + (c.faltam ? ` Mantenha o teste até a amostra planejada para confirmar (faltam ~${fmtInt(c.faltam)} por braço).` : '');
    } else {
        tom = 'yellow';
        titulo = 'Ainda sem diferença confiável na conversão';
        texto = c.faltam
            ? `${fmtTaxa(c.taxa_a)} × ${fmtTaxa(c.taxa_b)} (p = ${fmtP(c.p_valor)}). Para confirmar uma diferença deste tamanho faltam ~${fmtInt(c.faltam)} contatos com a janela fechada por braço.`
            : `${fmtTaxa(c.taxa_a)} × ${fmtTaxa(c.taxa_b)} até aqui (p = ${fmtP(c.p_valor)}).`;
    }

    const alvo = c.amostra_necessaria || minimo;
    const progresso = alvo ? Math.min(100, (c.menor_amostra / alvo) * 100) : 0;
    const corTom = { gray: 'gray.400', green: 'green.500', yellow: 'yellow.400' }[tom];

    return (
        <Card titulo="Quem converte mais" subtitulo={`Pedido no site em até ${janela}h depois da última resposta do bot — só contatos com a janela fechada`}>
            <Box borderLeftWidth="4px" borderLeftColor={corTom} pl={3} mb={4}>
                <Text fontWeight="semibold">{titulo}</Text>
                <Text fontSize="sm" color="gray.600">{texto}</Text>
            </Box>
            <FaixaConfianca bracos={bracos} />
            <Box mt={4}>
                <Flex justify="space-between" mb={1} gap={2}>
                    <Text fontSize="xs" color="gray.500">
                        Amostra: {fmtInt(c.menor_amostra)} de {fmtInt(alvo)} contatos com janela fechada (menor braço)
                    </Text>
                    <Text fontSize="xs" color="gray.500">{fmtPct(progresso, 0)}</Text>
                </Flex>
                <Progress value={progresso} size="sm" borderRadius="full" colorScheme={progresso >= 100 ? 'green' : 'orange'} aria-label="Progresso da amostra" />
                <Text fontSize="xs" color="gray.400" mt={1}>
                    {c.amostra_necessaria
                        ? 'Alvo = contatos por braço para confirmar a diferença observada (95% de confiança, 80% de poder).'
                        : `Alvo mínimo de leitura: ${minimo} contatos por braço.`}
                </Text>
            </Box>
        </Card>
    );
};

/** Quanto cada contato custa em IA e quanto traz em pedidos, por braço. */
const CustoRetorno = ({ r }) => {
    const { bracos, cotacao_dolar: dolar } = r;
    const brl = (usd) => (vazio(usd) || !dolar ? null : usd * dolar);
    const linhas = VARIANTES.map((v) => {
        const b = bracos[v];
        const custoBrl = brl(b.custo_por_contato_maduro_usd);
        return {
            v,
            b,
            custoUsd: b.custo_por_contato_maduro_usd,
            custoBrl,
            receita: b.receita_por_contato_brl,
            saldo: !vazio(b.receita_por_contato_brl) && custoBrl !== null ? b.receita_por_contato_brl - custoBrl : null,
        };
    });
    const [a, b] = linhas;
    const resumo = (() => {
        if (a.custoBrl === null || b.custoBrl === null || vazio(a.receita) || vazio(b.receita)) return null;
        const dCusto = a.custoBrl - b.custoBrl;
        const dReceita = a.receita - b.receita;
        return `Cada contato no ${a.b.rotulo} custou ${fmtBrl(Math.abs(dCusto))} ${dCusto >= 0 ? 'a mais' : 'a menos'} em IA `
            + `e trouxe ${fmtBrl(Math.abs(dReceita))} ${dReceita >= 0 ? 'a mais' : 'a menos'} em pedidos que no ${b.b.rotulo}.`;
    })();

    const Linha = ({ label, valores, destaque, nota }) => (
        <Grid templateColumns="1fr 92px 92px" gap={2} py={2} borderTopWidth="1px" borderColor="gray.100" alignItems="center"
            sx={{ fontVariantNumeric: 'tabular-nums' }}>
            <Box minW={0}>
                <Text fontSize="sm">{label}</Text>
                {nota && <Text fontSize="xs" color="gray.500">{nota}</Text>}
            </Box>
            {valores.map((t, i) => (
                <Text key={VARIANTES[i]} fontSize="sm" textAlign="right" fontWeight={destaque ? 'semibold' : 'normal'}>{t}</Text>
            ))}
        </Grid>
    );

    return (
        <Card titulo="Custo × retorno por contato" subtitulo="Contatos com a janela fechada · receita = soma dos itens, não é lucro">
            <Grid templateColumns="1fr 92px 92px" gap={2} pb={1}>
                <Box />
                {VARIANTES.map((v) => (
                    <HStack key={v} spacing={1} justify="flex-end"><Ponto cor={COR[v]} tamanho="8px" /><Text fontSize="xs" color="gray.600">{v}</Text></HStack>
                ))}
            </Grid>
            <Linha label="Custo de IA" nota={dolar ? `PTAX ${fmtNum(dolar, 2)}` : 'em dólar'}
                valores={linhas.map((l) => (dolar ? fmtBrl(l.custoBrl) : fmtUsd(l.custoUsd, 3)))} />
            <Linha label="Receita em pedidos" valores={linhas.map((l) => fmtBrl(l.receita))} />
            {dolar ? (
                <Linha label="Receita − custo de IA" destaque valores={linhas.map((l) => fmtBrl(l.saldo))} />
            ) : (
                <Text fontSize="xs" color="gray.500" pt={2}>Sem a cotação do dólar agora: o saldo em reais aparece quando a PTAX responder.</Text>
            )}
            {resumo && <Text fontSize="sm" color="gray.700" mt={3}>{resumo}</Text>}
        </Card>
    );
};

// ─────────────────────────── Comparativo ───────────────────────────

const SECOES = [
    {
        titulo: 'Conversão',
        linhas: [
            { label: 'Taxa de conversão', nota: 'compraram ÷ janela fechada', valor: (b) => b.taxa_conversao, fmt: fmtTaxa, melhor: 'maior', delta: 'pp' },
            { label: 'Compraram (janela fechada)', valor: (b) => b.convertidos_maduros, texto: (b) => `${fmtInt(b.convertidos_maduros)} de ${fmtInt(b.maduros)}` },
            { label: 'Conversão parcial', nota: 'inclui quem ainda está na janela', valor: (b) => b.taxa_conversao_parcial, fmt: fmtTaxa, melhor: 'maior', delta: 'pp' },
            { label: 'Pedidos atribuídos', valor: (b) => b.pedidos, fmt: fmtInt, melhor: 'maior' },
            { label: 'Receita atribuída', valor: (b) => b.receita_brl, fmt: (v) => fmtBrl(v, 0), melhor: 'maior' },
            { label: 'Receita por contato', nota: 'janela fechada', valor: (b) => b.receita_por_contato_brl, fmt: fmtBrl, melhor: 'maior' },
            { label: 'Ticket médio', valor: (b) => b.ticket_medio_brl, fmt: (v) => fmtBrl(v, 0) },
            { label: 'Recebeu carrinho → comprou', nota: 'janela fechada', valor: (b) => b.taxa_carrinho_compra, fmt: fmtTaxa, melhor: 'maior', delta: 'pp' },
        ],
    },
    {
        titulo: 'Custo de IA',
        linhas: [
            { label: 'Custo total', valor: (b) => b.custo_usd, fmt: (v) => fmtUsd(v, 2) },
            { label: 'Custo por resposta', valor: (b) => b.custo_por_resposta_usd, fmt: (v) => fmtUsd(v, 3), melhor: 'menor' },
            { label: 'Custo por contato', valor: (b) => b.custo_por_contato_usd, fmt: (v) => fmtUsd(v, 3), melhor: 'menor' },
            { label: 'Custo por pedido', valor: (b) => b.custo_por_pedido_usd, fmt: (v) => fmtUsd(v, 2), melhor: 'menor' },
            { label: 'Receita por US$ 1 de IA', valor: (b) => b.receita_por_dolar_brl, fmt: (v) => fmtBrl(v, 0), melhor: 'maior' },
            { label: 'Iterações por resposta', nota: 'idas e voltas com as tools', valor: (b) => b.iteracoes_media, fmt: (v) => fmtNum(v, 2) },
            { label: 'Recusas refeitas no Opus', nota: 'o Sonnet recusou e o Opus refez', valor: (b) => b.fallback_recusa, texto: (b) => `${fmtInt(b.fallback_recusa)} (${fmtPct(b.pct_fallback_recusa)})` },
        ],
    },
    {
        titulo: 'Velocidade',
        linhas: [
            { label: 'Tempo para gerar (mediana)', nota: 'da chamada até a resposta pronta', valor: (b) => b.tempo_resposta?.p50_s, fmt: fmtSeg, melhor: 'menor' },
            { label: 'Tempo — 90% até', valor: (b) => b.tempo_resposta?.p90_s, fmt: fmtSeg, melhor: 'menor' },
            { label: 'Tempo médio', valor: (b) => b.tempo_resposta?.media_s, fmt: fmtSeg, melhor: 'menor' },
        ],
    },
    {
        titulo: 'Como atende',
        linhas: [
            { label: 'Respostas por contato', valor: (b) => b.respostas_por_contato, fmt: (v) => fmtNum(v, 2) },
            { label: 'Tools por resposta', valor: (b) => b.tools_por_resposta, fmt: (v) => fmtNum(v, 2) },
            { label: 'Respostas sem tool', valor: (b) => b.pct_sem_tool, fmt: fmtPct },
            { label: 'Tamanho da resposta', nota: 'caracteres, média', valor: (b) => b.chars_medio, fmt: fmtInt, melhor: 'menor' },
            { label: 'Linhas por resposta', valor: (b) => b.linhas_medio, fmt: (v) => fmtNum(v, 1), melhor: 'menor' },
            { label: 'Com link de produto', nota: '% das respostas', valor: (b) => b.pct_link_produto, fmt: fmtPct, melhor: 'maior' },
            { label: 'Com link de carrinho', nota: '% das respostas', valor: (b) => b.pct_carrinho, fmt: fmtPct, melhor: 'maior' },
            { label: 'Com link do Mercado Livre', nota: '% das respostas', valor: (b) => b.pct_mercado_livre, fmt: fmtPct },
            { label: 'Disse "não temos"', nota: '% das respostas', valor: (b) => b.pct_negativa, fmt: fmtPct, melhor: 'menor' },
            { label: 'Contatos transferidos', nota: '% dos contatos', valor: (b) => b.pct_contatos_transferidos, fmt: fmtPct },
            { label: 'Tarefas para a equipe', nota: 'por 100 contatos', valor: (b) => b.solicitacoes_por_100_contatos, fmt: (v) => fmtNum(v, 1) },
        ],
    },
    {
        titulo: 'Volume e equilíbrio do sorteio',
        linhas: [
            { label: 'Contatos sorteados', valor: (b) => b.contatos, fmt: fmtInt },
            { label: 'Contatos com resposta enviada', valor: (b) => b.contatos_atendidos, fmt: fmtInt },
            { label: 'Respostas geradas', valor: (b) => b.respostas, fmt: fmtInt },
            { label: 'Entregas de solicitação', valor: (b) => b.entregas, fmt: fmtInt },
            { label: 'Falha no envio', nota: '% das respostas', valor: (b) => b.pct_erro_envio, fmt: fmtPct, melhor: 'menor' },
            { label: 'Já falavam com o bot', nota: 'nos 30 dias antes — deve ficar parecido', valor: (b) => b.pct_recorrentes, fmt: fmtPct },
            { label: 'Sem telefone', nota: 'ficam fora da conversão', valor: (b) => b.sem_telefone, fmt: fmtInt },
        ],
    },
];

const COLUNAS_COMPARATIVO = { base: '1fr 96px 96px', md: '1fr 132px 132px 92px' };

const CelulaValor = ({ texto, melhor }) => (
    <Text fontSize="sm" textAlign="right" fontWeight={melhor ? 'bold' : 'normal'} color={melhor ? COR_MELHOR : 'gray.800'}>
        {melhor && <Text as="span" fontSize="xs" mr={1} aria-label="melhor">▲</Text>}
        {texto}
    </Text>
);

const TabelaComparativa = ({ bracos }) => (
    <Card
        titulo="Lado a lado"
        subtitulo="▲ = melhor valor no período, sem teste estatístico (a conversão tem o veredito acima)"
        extra={<LegendaBracos bracos={bracos} />}
    >
        <Grid templateColumns={COLUNAS_COMPARATIVO} gap={2} pb={2}>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">Métrica</Text>
            {VARIANTES.map((v) => (
                <HStack key={v} spacing={1} justify="flex-end" minW={0}>
                    <Ponto cor={COR[v]} tamanho="8px" />
                    <Text fontSize="xs" color="gray.600" noOfLines={1} display={{ base: 'none', md: 'block' }}>{nomeBraco(bracos[v], v)}</Text>
                    <Text fontSize="xs" color="gray.600" display={{ base: 'block', md: 'none' }}>{v}</Text>
                </HStack>
            ))}
            <Text display={{ base: 'none', md: 'block' }} fontSize="xs" color="gray.500" textAlign="right">A vs B</Text>
        </Grid>
        {SECOES.map((secao) => (
            <Box key={secao.titulo} mt={3}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1}>{secao.titulo}</Text>
                {secao.linhas.map((l) => {
                    const vA = l.valor(bracos.A);
                    const vB = l.valor(bracos.B);
                    const melhor = qualMelhor(l.melhor, vA, vB);
                    const texto = (b, v) => (l.texto ? l.texto(b) : l.fmt(v));
                    return (
                        <Grid key={l.label} templateColumns={COLUNAS_COMPARATIVO} gap={2} py={1.5}
                            borderTopWidth="1px" borderColor="gray.100" alignItems="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            <Box minW={0}>
                                <Text fontSize="sm">{l.label}</Text>
                                {l.nota && <Text fontSize="xs" color="gray.500">{l.nota}</Text>}
                            </Box>
                            <CelulaValor texto={texto(bracos.A, vA)} melhor={melhor === 'A'} />
                            <CelulaValor texto={texto(bracos.B, vB)} melhor={melhor === 'B'} />
                            <Text display={{ base: 'none', md: 'block' }} fontSize="xs" color="gray.500" textAlign="right">
                                {l.texto ? '' : textoDelta(l.delta, vA, vB)}
                            </Text>
                        </Grid>
                    );
                })}
            </Box>
        ))}
    </Card>
);

// ─────────────────────────── Tools, custo por tipo, solicitações ───────────────────────────

const UsoTools = ({ bracos }) => {
    const linhas = useMemo(() => {
        const mapa = {};
        VARIANTES.forEach((v) => {
            (bracos[v].tools || []).forEach((t) => {
                mapa[t.nome] = mapa[t.nome] || { nome: t.nome };
                mapa[t.nome][v] = t;
            });
        });
        return Object.values(mapa).sort((x, y) =>
            ((y.A?.por_100_respostas || 0) + (y.B?.por_100_respostas || 0)) - ((x.A?.por_100_respostas || 0) + (x.B?.por_100_respostas || 0)));
    }, [bracos]);
    const maximo = Math.max(1, ...linhas.flatMap((l) => VARIANTES.map((v) => l[v]?.por_100_respostas || 0)));
    const medidas = VARIANTES.map((v) => bracos[v].respostas_medidas || 0);
    const parcial = VARIANTES.some((v) => (bracos[v].respostas_medidas || 0) < (bracos[v].respostas || 0));

    return (
        <Card titulo="Uso de tools" subtitulo="Chamadas por 100 respostas · % das respostas que usaram a tool" extra={<LegendaBracos bracos={bracos} />}>
            <SimpleGrid columns={2} spacing={3} mb={3}>
                {[
                    { label: 'Tools por resposta', fmt: (b) => fmtNum(b.tools_por_resposta, 2) },
                    { label: 'Respostas sem tool', fmt: (b) => fmtPct(b.pct_sem_tool) },
                ].map((s) => (
                    <Box key={s.label} bg="gray.50" borderRadius="md" p={2.5}>
                        <Text fontSize="xs" color="gray.500">{s.label}</Text>
                        {VARIANTES.map((v) => (
                            <HStack key={v} spacing={1.5} mt={0.5}>
                                <Ponto cor={COR[v]} tamanho="8px" />
                                <Text fontSize="sm" fontWeight="semibold">{s.fmt(bracos[v])}</Text>
                                <Text fontSize="xs" color="gray.500">{v}</Text>
                            </HStack>
                        ))}
                    </Box>
                ))}
            </SimpleGrid>
            {linhas.length === 0 ? (
                <Text fontSize="sm" color="gray.500" py={4} textAlign="center">Nenhuma tool registrada ainda.</Text>
            ) : linhas.map((l) => (
                <Box key={l.nome} py={2} borderTopWidth="1px" borderColor="gray.100">
                    <Text fontSize="sm" fontWeight="medium">{TOOL_LABEL[l.nome] || l.nome}</Text>
                    {VARIANTES.map((v) => {
                        const t = l[v];
                        const por100 = t?.por_100_respostas || 0;
                        const legenda = `${nomeBraco(bracos[v], v)}: ${fmtNum(por100, 1)} chamadas por 100 respostas, em ${fmtPct(t?.pct_respostas || 0, 0)} das respostas`;
                        return (
                            <Flex key={v} align="center" gap={2} mt={1} aria-label={legenda}>
                                <Text fontSize="xs" color="gray.500" w="12px">{v}</Text>
                                <Tooltip label={legenda} hasArrow>
                                    <Box flex="1" h="8px" bg="gray.100" borderRadius="full" overflow="hidden">
                                        <Box h="100%" w={`${(por100 / maximo) * 100}%`} bg={COR[v]} borderRadius="full" />
                                    </Box>
                                </Tooltip>
                                <Text fontSize="xs" color="gray.700" w={{ base: '104px', md: '128px' }} textAlign="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {fmtNum(por100, por100 >= 10 ? 0 : 1)}/100 · {fmtPct(t?.pct_respostas || 0, 0)}
                                </Text>
                            </Flex>
                        );
                    })}
                </Box>
            ))}
            {parcial && (
                <Text fontSize="xs" color="gray.400" mt={2}>
                    Base: {fmtInt(medidas[0])} (A) e {fmtInt(medidas[1])} (B) respostas com tools registradas.
                </Text>
            )}
        </Card>
    );
};

const CustoPorTipo = ({ bracos }) => (
    <Card titulo="Para onde vai o dinheiro" subtitulo="Custo por tipo de token em cada braço">
        <TableContainer>
            <Table size="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                <Thead>
                    <Tr>
                        <Th px={2}>Tipo</Th>
                        {VARIANTES.map((v) => (
                            <Th key={v} px={2} isNumeric>
                                <HStack spacing={1} justify="flex-end"><Ponto cor={COR[v]} tamanho="8px" /><span>{v}</span></HStack>
                            </Th>
                        ))}
                    </Tr>
                </Thead>
                <Tbody>
                    {TIPOS_TOKEN.map((t) => (
                        <Tr key={t.chave}>
                            <Td px={2}>
                                <Text fontSize="sm">{t.label}</Text>
                                <Text fontSize="xs" color="gray.500">{t.nota}</Text>
                            </Td>
                            {VARIANTES.map((v) => {
                                const total = Number(bracos[v].custo_usd || 0);
                                const valor = Number(bracos[v].custo_por_tipo?.[t.chave] || 0);
                                return (
                                    <Td key={v} px={2} isNumeric>
                                        <Text fontSize="sm">{fmtUsd(valor)}</Text>
                                        <Text fontSize="xs" color="gray.500">{fmtPct(total ? (valor / total) * 100 : 0, 0)}</Text>
                                    </Td>
                                );
                            })}
                        </Tr>
                    ))}
                    <Tr>
                        <Td px={2} fontWeight="semibold">Total</Td>
                        {VARIANTES.map((v) => (
                            <Td key={v} px={2} isNumeric fontWeight="semibold">{fmtUsd(bracos[v].custo_usd)}</Td>
                        ))}
                    </Tr>
                </Tbody>
            </Table>
        </TableContainer>
    </Card>
);

const Solicitacoes = ({ bracos, tipos }) => {
    const nomes = Array.from(new Set(VARIANTES.flatMap((v) => Object.keys(bracos[v].solicitacoes_por_tipo || {})))).sort();
    return (
        <Card titulo="Tarefas abertas para a equipe" subtitulo="solicitar_tarefa_humana depois do sorteio">
            {nomes.length === 0 ? (
                <Text fontSize="sm" color="gray.500" py={2}>Nenhuma tarefa aberta no teste até agora.</Text>
            ) : (
                <TableContainer>
                    <Table size="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        <Thead>
                            <Tr>
                                <Th px={2}>Tipo</Th>
                                {VARIANTES.map((v) => (
                                    <Th key={v} px={2} isNumeric>
                                        <HStack spacing={1} justify="flex-end"><Ponto cor={COR[v]} tamanho="8px" /><span>{v}</span></HStack>
                                    </Th>
                                ))}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {nomes.map((n) => (
                                <Tr key={n}>
                                    <Td px={2}>{tipos?.[n] || n}</Td>
                                    {VARIANTES.map((v) => <Td key={v} px={2} isNumeric>{fmtInt(bracos[v].solicitacoes_por_tipo?.[n] || 0)}</Td>)}
                                </Tr>
                            ))}
                            <Tr>
                                <Td px={2}><Text fontSize="sm">Por 100 contatos</Text></Td>
                                {VARIANTES.map((v) => <Td key={v} px={2} isNumeric fontWeight="semibold">{fmtNum(bracos[v].solicitacoes_por_100_contatos, 1)}</Td>)}
                            </Tr>
                        </Tbody>
                    </Table>
                </TableContainer>
            )}
        </Card>
    );
};

// ─────────────────────────── Dia a dia ───────────────────────────

const METRICAS_DIA = [
    { id: 'custo', label: 'Custo', fmt: (v) => fmtUsd(v), eixo: (v) => `US$ ${v}` },
    { id: 'respostas', label: 'Respostas', fmt: fmtInt, eixo: (v) => v },
    { id: 'contatos', label: 'Contatos novos', fmt: fmtInt, eixo: (v) => v },
    { id: 'conversoes', label: 'Compras', fmt: fmtInt, eixo: (v) => v },
];

const GraficoDiario = ({ serie, bracos }) => {
    const [metrica, setMetrica] = useState('custo');
    const m = METRICAS_DIA.find((x) => x.id === metrica);

    const data = useMemo(() => ({
        labels: serie.map((d) => fmtDia(d.dia)),
        datasets: VARIANTES.map((v) => ({
            label: nomeBraco(bracos[v], v),
            data: serie.map((d) => Number(d[`${metrica}_${v}`] || 0)),
            backgroundColor: COR[v],
            borderRadius: 4,
            borderSkipped: 'start',
            maxBarThickness: 24,
            barPercentage: 0.9,
            categoryPercentage: 0.7,
        })),
    }), [serie, bracos, metrica]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                position: 'top',
                align: 'start',
                labels: { usePointStyle: true, pointStyle: 'circle', boxWidth: 8, boxHeight: 8, padding: 14 },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ${ctx.dataset.label}: ${m.fmt(ctx.parsed.y)}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { color: GRADE },
                ticks: { color: TINTA_MUTED, autoSkip: true, maxRotation: 0, maxTicksLimit: 12 },
            },
            y: {
                beginAtZero: true,
                grid: { color: GRADE, drawTicks: false },
                border: { display: false },
                ticks: { color: TINTA_MUTED, padding: 8, precision: metrica === 'custo' ? undefined : 0, callback: m.eixo },
            },
        },
    }), [m, metrica]);

    return (
        <Card
            titulo="Dia a dia"
            subtitulo={metrica === 'conversoes' ? 'Contatos que compraram, pelo dia em que foram sorteados' : 'Por braço'}
            extra={(
                <ButtonGroup size="xs" isAttached variant="outline" flexWrap="wrap">
                    {METRICAS_DIA.map((x) => (
                        <Button
                            key={x.id}
                            onClick={() => setMetrica(x.id)}
                            bg={metrica === x.id ? ACCENT : 'white'}
                            color={metrica === x.id ? 'white' : 'gray.600'}
                            _hover={metrica === x.id ? { bg: ACCENT } : { bg: 'gray.50' }}
                            aria-pressed={metrica === x.id}
                        >
                            {x.label}
                        </Button>
                    ))}
                </ButtonGroup>
            )}
        >
            {serie.length === 0 ? (
                <Text color="gray.500" py={8} textAlign="center">Sem dias no período.</Text>
            ) : (
                <Box h={{ base: '260px', md: '320px' }} role="img" aria-label={`${m.label} por dia, por braço`}>
                    <Bar data={data} options={options} />
                </Box>
            )}
        </Card>
    );
};

// ─────────────────────────── Contatos ───────────────────────────

const ListaContatos = ({ contatos, total, limite, bracos }) => {
    const [braco, setBraco] = useState('todos');
    const [situacao, setSituacao] = useState('todos');
    const [busca, setBusca] = useState('');
    const [mostrar, setMostrar] = useState(POR_PAGINA);

    useEffect(() => { setMostrar(POR_PAGINA); }, [braco, situacao, busca]);

    const filtrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const digitos = termo.replace(/\D/g, '');
        return contatos.filter((c) => (braco === 'todos' || c.variante === braco)
            && (situacao === 'todos' || c.status === situacao)
            && (!termo
                || String(c.nome || '').toLowerCase().includes(termo)
                || (digitos.length >= 3 && String(c.telefone || '').includes(digitos))
                || String(c.poli_contact_id).includes(termo)));
    }, [contatos, braco, situacao, busca]);
    const visiveis = filtrados.slice(0, mostrar);

    const SituacaoBadge = ({ c }) => {
        const meta = STATUS_CONTATO[c.status] || { label: c.status, scheme: 'gray' };
        const pedidos = (c.pedidos || []).map((p) => `${p.id_origem || `#${p.id}`} · ${fmtDataHora(p.data)} · ${fmtBrl(p.valor)}`).join('\n');
        return (
            <Tooltip label={pedidos || `Janela até ${fmtDataHora(c.janela_ate)}`} hasArrow whiteSpace="pre-line">
                <Badge colorScheme={meta.scheme} fontSize="0.7em" px={2} py={0.5} tabIndex={0}>{meta.label}</Badge>
            </Tooltip>
        );
    };

    return (
        <Card
            titulo="Contatos do teste"
            subtitulo={total > contatos.length ? `Mostrando os ${fmtInt(limite)} mais recentes de ${fmtInt(total)}` : `${fmtInt(total)} contatos sorteados`}
        >
            <Flex gap={2} mb={3} wrap="wrap">
                <ButtonGroup size="sm" isAttached variant="outline">
                    {['todos', ...VARIANTES].map((v) => (
                        <Button key={v} onClick={() => setBraco(v)} aria-pressed={braco === v}
                            bg={braco === v ? ACCENT : 'white'} color={braco === v ? 'white' : 'gray.600'}
                            _hover={braco === v ? { bg: ACCENT } : { bg: 'gray.50' }}>
                            {v === 'todos' ? 'Todos' : v}
                        </Button>
                    ))}
                </ButtonGroup>
                <Select size="sm" w={{ base: '100%', sm: '170px' }} value={situacao} onChange={(e) => setSituacao(e.target.value)} aria-label="Filtrar por situação" bg="white">
                    <option value="todos">Todas as situações</option>
                    {Object.entries(STATUS_CONTATO).map(([k, meta]) => <option key={k} value={k}>{meta.label}</option>)}
                </Select>
                <InputGroup size="sm" w={{ base: '100%', sm: '240px' }}>
                    <InputLeftElement pointerEvents="none"><Search size={14} color="#A0AEC0" /></InputLeftElement>
                    <Input placeholder="Nome, telefone ou id" value={busca} onChange={(e) => setBusca(e.target.value)} bg="white" aria-label="Buscar contato" />
                </InputGroup>
            </Flex>

            {filtrados.length === 0 ? (
                <Text fontSize="sm" color="gray.500" py={6} textAlign="center">Nenhum contato com esses filtros.</Text>
            ) : (
                <>
                    {/* Celular e tablet: cartões (9 colunas não cabem abaixo de ~1000px) */}
                    <Box display={{ base: 'block', lg: 'none' }}>
                        {visiveis.map((c) => (
                            <Box key={c.poli_contact_id} py={2.5} borderTopWidth="1px" borderColor="gray.100">
                                <Flex justify="space-between" align="center" gap={2}>
                                    <HStack spacing={2} minW={0}>
                                        <Ponto cor={COR[c.variante]} />
                                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{c.nome || `Contato ${c.poli_contact_id}`}</Text>
                                    </HStack>
                                    <SituacaoBadge c={c} />
                                </Flex>
                                <Text fontSize="xs" color="gray.500" mt={0.5}>
                                    {nomeBraco(bracos[c.variante], c.variante)} · {fmtDataHora(c.atribuido_em)} · {c.telefone || 'sem telefone'}
                                </Text>
                                <Text fontSize="xs" color="gray.600" mt={0.5} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {fmtInt(c.respostas)} resp. · {fmtInt(c.tools)} tools · {fmtUsd(c.custo_usd, 3)}
                                    {c.valor_pedidos > 0 ? ` · pedido ${fmtBrl(c.valor_pedidos)}` : ''}
                                </Text>
                            </Box>
                        ))}
                    </Box>

                    {/* Desktop: tabela */}
                    <TableContainer display={{ base: 'none', lg: 'block' }}>
                        <Table size="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            <Thead>
                                <Tr>
                                    <Th>Contato</Th>
                                    <Th>Braço</Th>
                                    <Th>Sorteado</Th>
                                    <Th isNumeric>Respostas</Th>
                                    <Th isNumeric>Tools</Th>
                                    <Th isNumeric>Tarefas</Th>
                                    <Th isNumeric>Custo</Th>
                                    <Th>Situação</Th>
                                    <Th isNumeric>Pedido</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {visiveis.map((c) => (
                                    <Tr key={c.poli_contact_id}>
                                        <Td>
                                            <Tooltip label={`Poli contact_id ${c.poli_contact_id}`} hasArrow>
                                                <Text noOfLines={1} maxW="220px">{c.nome || `Contato ${c.poli_contact_id}`}</Text>
                                            </Tooltip>
                                            <Text fontSize="xs" color="gray.500">{c.telefone || 'sem telefone'}</Text>
                                        </Td>
                                        <Td>
                                            <HStack spacing={1.5}><Ponto cor={COR[c.variante]} tamanho="8px" /><Text fontSize="sm">{nomeBraco(bracos[c.variante], c.variante)}</Text></HStack>
                                        </Td>
                                        <Td>{fmtDataHora(c.atribuido_em)}</Td>
                                        <Td isNumeric>{fmtInt(c.respostas)}{c.erros > 0 && <Text as="span" fontSize="xs" color="red.500"> ({c.erros} erro)</Text>}</Td>
                                        <Td isNumeric>{fmtInt(c.tools)}</Td>
                                        <Td isNumeric>{fmtInt(c.solicitacoes)}</Td>
                                        <Td isNumeric>{fmtUsd(c.custo_usd, 3)}</Td>
                                        <Td><SituacaoBadge c={c} /></Td>
                                        <Td isNumeric>{c.valor_pedidos > 0 ? fmtBrl(c.valor_pedidos) : '—'}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>

                    <Flex justify="space-between" align="center" mt={3} gap={2} wrap="wrap">
                        <Text fontSize="xs" color="gray.500">{fmtInt(visiveis.length)} de {fmtInt(filtrados.length)}</Text>
                        {visiveis.length < filtrados.length && (
                            <Button size="sm" variant="outline" onClick={() => setMostrar((n) => n + POR_PAGINA)}>Mostrar mais</Button>
                        )}
                    </Flex>
                </>
            )}
        </Card>
    );
};

// ─────────────────────────── Metodologia ───────────────────────────

const Metodologia = ({ parametros }) => {
    const janela = parametros?.janela_conversao_horas || 72;
    const minimo = parametros?.amostra_minima || 100;
    const dias = parametros?.dias_recorrente || 30;
    return (
        <Accordion allowToggle bg="white" borderWidth="1px" borderRadius="lg">
            <AccordionItem border="none">
                <AccordionButton px={4} py={3}>
                    <Box flex="1" textAlign="left" fontWeight="semibold">Como o teste funciona e como cada número é calculado</Box>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={4} pb={4}>
                    <UnorderedList spacing={1.5} fontSize="sm" color="gray.700">
                        <ListItem><b>Sorteio por contato.</b> Na primeira resposta do bot durante o teste, o contato vai para o braço com menos contatos (empate é sorteado), então a divisão fica 50/50. Ele fica no mesmo modelo até o teste acabar, inclusive nas entregas de solicitação. Conversa que já estava em andamento entra a partir da próxima resposta.</ListItem>
                        <ListItem><b>O que muda entre os braços:</b> só o modelo. Prompt, tools, esforço de raciocínio e regras são os mesmos. Recusa de segurança no Sonnet continua sendo refeita no Opus (aparece em &quot;Recusas refeitas no Opus&quot;).</ListItem>
                        <ListItem><b>Conversão:</b> pedido do site (não cancelado) com o mesmo telefone (DDD + 8 últimos dígitos), feito entre o sorteio e {janela}h depois da última resposta do bot no teste. Venda do Mercado Livre não entra: ela chega sem telefone.</ListItem>
                        <ListItem><b>Janela fechada:</b> a taxa só usa contatos cujas {janela}h já passaram — quem ainda está na janela entra só na &quot;conversão parcial&quot;, que tende a subir com o tempo.</ListItem>
                        <ListItem><b>Veredito:</b> teste z de duas proporções com intervalo de 95% (Wilson). Com p &lt; 0,05 a diferença dificilmente é acaso. Antes de {minimo} contatos com janela fechada por braço não há veredito. Olhar todo dia e parar no primeiro p &lt; 0,05 aumenta a chance de falso positivo: decida a duração pela amostra planejada.</ListItem>
                        <ListItem><b>Custo:</b> tokens de cada resposta e entrega no modelo do braço (tabela de preços da Anthropic, em dólar; reais pela PTAX do dia). Triagem e varredura têm modelo próprio e ficam fora.</ListItem>
                        <ListItem><b>Tools e tempo:</b> registrados em cada resposta desde 09/10/2026. O tempo é o da geração (loop com as tools), sem contar a espera do poll da Poli.</ListItem>
                        <ListItem><b>Conteúdo:</b> lido das respostas enviadas (links de produto, carrinho e Mercado Livre, &quot;não temos&quot;, tamanho). &quot;Já falavam com o bot&quot; = resposta do bot nos {dias} dias antes do sorteio; deve ficar parecido nos dois braços se o sorteio equilibrou.</ListItem>
                        <ListItem><b>Encerrar</b> devolve todo mundo ao modelo padrão na resposta seguinte. O resultado continua salvo aqui.</ListItem>
                    </UnorderedList>
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );
};

// ─────────────────────────── Iniciar / encerrar ───────────────────────────

const ModalIniciar = ({ isOpen, onClose, modelos, onConfirmar, salvando }) => {
    const ids = Object.keys(modelos || {});
    const padraoA = ids.includes('claude-opus-5-5') ? 'claude-opus-5-5' : ids[0];
    const padraoB = ids.includes('claude-sonnet-5-5') ? 'claude-sonnet-5-5' : ids[1];
    const [modeloA, setModeloA] = useState(padraoA || '');
    const [modeloB, setModeloB] = useState(padraoB || '');
    const [nome, setNome] = useState('');
    const [observacao, setObservacao] = useState('');

    useEffect(() => {
        if (isOpen) {
            setModeloA(padraoA || '');
            setModeloB(padraoB || '');
            setNome('');
            setObservacao('');
        }
    }, [isOpen, padraoA, padraoB]);

    const iguais = modeloA && modeloA === modeloB;
    const rotulo = (m) => modelos?.[m] || m;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Iniciar teste A/B</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        <FormControl>
                            <FormLabel fontSize="sm"><HStack spacing={1.5}><Ponto cor={COR.A} tamanho="8px" /><span>Braço A</span></HStack></FormLabel>
                            <Select value={modeloA} onChange={(e) => setModeloA(e.target.value)}>
                                {ids.map((m) => <option key={m} value={m}>{rotulo(m)}</option>)}
                            </Select>
                        </FormControl>
                        <FormControl isInvalid={iguais}>
                            <FormLabel fontSize="sm"><HStack spacing={1.5}><Ponto cor={COR.B} tamanho="8px" /><span>Braço B</span></HStack></FormLabel>
                            <Select value={modeloB} onChange={(e) => setModeloB(e.target.value)}>
                                {ids.map((m) => <option key={m} value={m}>{rotulo(m)}</option>)}
                            </Select>
                        </FormControl>
                    </SimpleGrid>
                    {iguais && <Text fontSize="xs" color="red.500" mt={1}>Escolha modelos diferentes para os dois braços.</Text>}
                    <FormControl mt={4}>
                        <FormLabel fontSize="sm">Nome (opcional)</FormLabel>
                        <Input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120}
                            placeholder={`${rotulo(modeloA)} × ${rotulo(modeloB)}`} />
                    </FormControl>
                    <FormControl mt={3}>
                        <FormLabel fontSize="sm">Observação (opcional)</FormLabel>
                        <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)} maxLength={2000} rows={2}
                            placeholder="Ex.: decidir se o bot volta para o Opus" />
                        <FormHelperText fontSize="xs">Aparece no topo da tela enquanto o teste existir.</FormHelperText>
                    </FormControl>
                    <Alert status="info" borderRadius="md" mt={4} fontSize="sm" alignItems="flex-start">
                        <AlertIcon mt={0.5} />
                        <Box>
                            A partir da próxima resposta, cada contato que o bot atender é sorteado 50/50 e fica no mesmo
                            modelo até você encerrar. O Opus custa cerca do dobro por resposta: com metade dos contatos nele,
                            o gasto do bot sobe perto de 50% enquanto o teste estiver ligado.
                        </Box>
                    </Alert>
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                    <Button bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} leftIcon={<Play size={16} />}
                        isDisabled={!modeloA || !modeloB || iguais} isLoading={salvando}
                        onClick={() => onConfirmar({ nome: nome.trim() || null, modelo_a: modeloA, modelo_b: modeloB, observacao: observacao.trim() || null })}>
                        Iniciar teste
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const DialogEncerrar = ({ isOpen, onClose, onConfirmar, salvando, rotuloPadrao }) => {
    const cancelRef = useRef();
    return (
        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} isCentered>
            <AlertDialogOverlay>
                <AlertDialogContent mx={4}>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">Encerrar o teste?</AlertDialogHeader>
                    <AlertDialogBody>
                        Da próxima resposta em diante, todos os contatos voltam para o {rotuloPadrao}. Os resultados
                        continuam salvos nesta tela. Não dá para retomar: um teste novo sorteia todo mundo de novo.
                    </AlertDialogBody>
                    <AlertDialogFooter gap={2}>
                        <Button ref={cancelRef} onClick={onClose}>Cancelar</Button>
                        <Button colorScheme="red" onClick={onConfirmar} isLoading={salvando} leftIcon={<Square size={14} />}>Encerrar</Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

// ─────────────────────────── Página ───────────────────────────

const mensagemErro = (e, padrao) => {
    if (e?.response?.status === 403) return 'Apenas administradores podem ver o teste A/B.';
    return e?.response?.data?.erro || e?.response?.data?.message || padrao;
};

const BotTesteAB = () => {
    const toast = useToast();
    const modalIniciar = useDisclosure();
    const dialogEncerrar = useDisclosure();

    const [lista, setLista] = useState(null);
    const [selecionadoId, setSelecionadoId] = useState(null);
    const [resultado, setResultado] = useState(null);
    const [carregandoLista, setCarregandoLista] = useState(true);
    const [carregandoResultado, setCarregandoResultado] = useState(false);
    const [erro, setErro] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const pedidoAtual = useRef(0);

    const testes = useMemo(() => lista?.testes || [], [lista]);
    const selecionado = testes.find((t) => t.id === selecionadoId) || null;
    const ativoExiste = testes.some((t) => t.status === 'ativo');
    const rotuloPadrao = lista?.modelos?.[lista?.modelo_padrao] || lista?.modelo_padrao || 'modelo padrão';

    const carregarLista = useCallback(async (selecionar = null) => {
        setCarregandoLista(true);
        try {
            const { data } = await getBotAbTestes();
            setLista(data);
            setErro(null);
            const ids = (data.testes || []).map((t) => t.id);
            setSelecionadoId((atual) => {
                if (selecionar && ids.includes(selecionar)) return selecionar;
                if (atual && ids.includes(atual)) return atual;
                const ativo = (data.testes || []).find((t) => t.status === 'ativo');
                return ativo ? ativo.id : (ids[0] ?? null);
            });
        } catch (e) {
            setErro(mensagemErro(e, 'Não foi possível carregar os testes A/B.'));
        } finally {
            setCarregandoLista(false);
        }
    }, []);

    const carregarResultado = useCallback(async (id) => {
        if (!id) {
            setResultado(null);
            return;
        }
        const pedido = ++pedidoAtual.current;
        setCarregandoResultado(true);
        try {
            const { data } = await getBotAbResultado(id);
            if (pedido === pedidoAtual.current) {
                setResultado(data);
                setErro(null);
            }
        } catch (e) {
            if (pedido === pedidoAtual.current) {
                setErro(mensagemErro(e, 'Não foi possível calcular o resultado do teste.'));
            }
        } finally {
            if (pedido === pedidoAtual.current) setCarregandoResultado(false);
        }
    }, []);

    useEffect(() => { carregarLista(); }, [carregarLista]);
    useEffect(() => { carregarResultado(selecionadoId); }, [carregarResultado, selecionadoId]);

    // Teste rodando: atualiza sozinho de tempos em tempos, só com a aba visível.
    useEffect(() => {
        if (!selecionado || selecionado.status !== 'ativo') return undefined;
        const timer = setInterval(() => {
            if (document.visibilityState === 'visible') carregarResultado(selecionado.id);
        }, ATUALIZAR_A_CADA_MS);
        return () => clearInterval(timer);
    }, [selecionado, carregarResultado]);

    const atualizar = () => {
        carregarLista();
        carregarResultado(selecionadoId);
    };

    const iniciar = async (payload) => {
        setSalvando(true);
        try {
            const { data } = await iniciarBotAbTeste(payload);
            toast({ title: 'Teste A/B iniciado', description: 'O sorteio vale a partir da próxima resposta do bot.', status: 'success', duration: 5000, isClosable: true });
            modalIniciar.onClose();
            await carregarLista(data?.id);
        } catch (e) {
            toast({ title: mensagemErro(e, 'Não foi possível iniciar o teste.'), status: 'error', duration: 6000, isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    const encerrar = async () => {
        if (!selecionado) return;
        setSalvando(true);
        try {
            await encerrarBotAbTeste(selecionado.id);
            toast({ title: 'Teste encerrado', description: `Todos voltam para o ${rotuloPadrao} na próxima resposta.`, status: 'success', duration: 5000, isClosable: true });
            dialogEncerrar.onClose();
            await carregarLista(selecionado.id);
            carregarResultado(selecionado.id);
        } catch (e) {
            toast({ title: mensagemErro(e, 'Não foi possível encerrar o teste.'), status: 'error', duration: 6000, isClosable: true });
        } finally {
            setSalvando(false);
        }
    };

    const r = resultado && resultado.teste?.id === selecionadoId ? resultado : null;
    const semContatos = r && r.total_contatos === 0;

    return (
        <Box ml={{ base: 0, md: '64px' }} pt="70px" px={{ base: 3, md: 6 }} pb={10} minH="100vh" bg="gray.50">
            <Box maxW="1280px" mx="auto">
                <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={4} gap={3} direction={{ base: 'column', md: 'row' }}>
                    <HStack spacing={3} align="flex-start" minW={0}>
                        <Box mt={1} color={ACCENT} flexShrink={0}><FlaskConical size={24} /></Box>
                        <Box minW={0}>
                            <Text as="h1" fontSize="2xl" fontWeight="bold" lineHeight="short">Teste A/B do Bot</Text>
                            <Text fontSize="sm" color="gray.600">
                                Dois modelos atendendo contatos sorteados: quem converte mais, quanto custa e como trabalha.
                            </Text>
                        </Box>
                    </HStack>
                    <HStack spacing={2} w={{ base: '100%', md: 'auto' }}>
                        {testes.length > 1 && (
                            <Select size="sm" bg="white" value={selecionadoId || ''} onChange={(e) => setSelecionadoId(Number(e.target.value))}
                                aria-label="Escolher teste" w={{ base: '100%', md: '260px' }}>
                                {testes.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        #{t.id} · {t.nome}{t.status === 'ativo' ? ' (em andamento)' : ''}
                                    </option>
                                ))}
                            </Select>
                        )}
                        <Button size="sm" leftIcon={<RefreshCw size={14} />} onClick={atualizar}
                            isLoading={carregandoLista || carregandoResultado} flexShrink={0} bg="white" variant="outline">
                            Atualizar
                        </Button>
                    </HStack>
                </Flex>

                {erro && (
                    <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
                        <AlertIcon />
                        <Box flex="1">{erro}</Box>
                        <Button size="xs" ml={2} onClick={atualizar}>Tentar de novo</Button>
                    </Alert>
                )}

                {carregandoLista && !lista ? (
                    <Flex justify="center" py={20}><Spinner size="lg" color={ACCENT} /></Flex>
                ) : lista && (
                    <>
                        <BannerStatus
                            teste={selecionado}
                            ativoExiste={ativoExiste}
                            rotuloPadrao={rotuloPadrao}
                            modelos={lista.modelos}
                            onIniciar={modalIniciar.onOpen}
                            onEncerrar={dialogEncerrar.onOpen}
                        />

                        {selecionado && !r && carregandoResultado && (
                            <Flex justify="center" py={16}><Spinner size="lg" color={ACCENT} /></Flex>
                        )}

                        {semContatos && (
                            <Box bg="white" borderWidth="1px" borderRadius="lg" p={6} mb={4} textAlign="center">
                                <Text fontWeight="semibold">Nenhum contato sorteado ainda</Text>
                                <Text fontSize="sm" color="gray.600" mt={1}>
                                    O primeiro aparece aqui assim que o bot responder alguém. A tela se atualiza sozinha a cada 2 minutos.
                                </Text>
                            </Box>
                        )}

                        {r && !semContatos && (
                            // Em recarga, segura o render anterior esmaecido — sem piscar esqueleto.
                            <Box opacity={carregandoResultado ? 0.55 : 1} transition="opacity .15s">
                                <Grid templateColumns={{ base: '1fr', lg: '3fr 2fr' }} gap={4} mb={4}>
                                    <VereditoConversao r={r} />
                                    <CustoRetorno r={r} />
                                </Grid>

                                <Box mb={4}><TabelaComparativa bracos={r.bracos} /></Box>

                                <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4} mb={4} alignItems="start">
                                    <UsoTools bracos={r.bracos} />
                                    <Box display="grid" gap={4}>
                                        <CustoPorTipo bracos={r.bracos} />
                                        <Solicitacoes bracos={r.bracos} tipos={r.tipos_solicitacao} />
                                    </Box>
                                </Grid>

                                <Box mb={4}><GraficoDiario serie={r.serie || []} bracos={r.bracos} /></Box>

                                <Box mb={4}>
                                    <ListaContatos
                                        contatos={r.contatos || []}
                                        total={r.total_contatos}
                                        limite={r.parametros?.limite_contatos}
                                        bracos={r.bracos}
                                    />
                                </Box>

                                <Text fontSize="xs" color="gray.400" mb={4}>
                                    Calculado em {fmtDataHora(r.gerado_em)} · {fmtNum(r.teste?.dias, 1)} dias de teste
                                </Text>
                            </Box>
                        )}

                        <Metodologia parametros={r?.parametros} />
                    </>
                )}
            </Box>

            <ModalIniciar
                isOpen={modalIniciar.isOpen}
                onClose={modalIniciar.onClose}
                modelos={lista?.modelos}
                onConfirmar={iniciar}
                salvando={salvando}
            />
            <DialogEncerrar
                isOpen={dialogEncerrar.isOpen}
                onClose={dialogEncerrar.onClose}
                onConfirmar={encerrar}
                salvando={salvando}
                rotuloPadrao={rotuloPadrao}
            />
        </Box>
    );
};

export default BotTesteAB;
